import React, { useState, useEffect } from 'react';
import api from '../api';
import { Plus, PencilSimple, Trash, FunnelSimple, Star } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const ServiceManagement = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState('_all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    categoria: '',
    nome: '',
    novaCategoria: '',
    nivel_tecnico: 1
  });

  const StarRating = ({ value, onChange, readOnly = false, size = 14 }) => (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(star => (
        <button key={star} type="button" onClick={() => !readOnly && onChange && onChange(star)}
          className={`${readOnly ? '' : 'cursor-pointer hover:scale-110'} transition-transform`} disabled={readOnly}>
          <Star size={size} weight={star <= value ? 'fill' : 'regular'} className={star <= value ? 'text-[#FFB800]' : 'text-zinc-600'} />
        </button>
      ))}
    </div>
  );

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const [svcRes, catRes] = await Promise.all([
        api.get('/services'),
        api.get('/services/categories')
      ]);
      setServices(svcRes.data);
      setCategories(catRes.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const categoria = formData.novaCategoria || formData.categoria;
    if (!categoria || !formData.nome) {
      alert('Preencha a categoria e o nome do servico');
      return;
    }

    try {
      await api.post('/services', { categoria, nome: formData.nome, nivel_tecnico: formData.nivel_tecnico });
      setIsCreateOpen(false);
      setFormData({ categoria: '', nome: '', novaCategoria: '', nivel_tecnico: 1 });
      fetchServices();
    } catch (error) {
      alert('Erro ao criar servico: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedService) return;

    const categoria = formData.novaCategoria || formData.categoria;
    try {
      await api.put(`/services/${selectedService.id}`, {
        categoria: categoria || undefined,
        nome: formData.nome || undefined,
        nivel_tecnico: formData.nivel_tecnico || undefined
      });
      setIsEditOpen(false);
      setSelectedService(null);
      setFormData({ categoria: '', nome: '', novaCategoria: '', nivel_tecnico: 1 });
      fetchServices();
    } catch (error) {
      alert('Erro ao atualizar servico: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async () => {
    if (!selectedService) return;

    try {
      await api.delete(`/services/${selectedService.id}`);
      setIsDeleteOpen(false);
      setSelectedService(null);
      fetchServices();
    } catch (error) {
      alert('Erro ao excluir servico: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleActive = async (service) => {
    try {
      await api.put(`/services/${service.id}`, { ativo: !service.ativo });
      fetchServices();
    } catch (error) {
      console.error('Error toggling service:', error);
    }
  };

  const openEdit = (service) => {
    setSelectedService(service);
    setFormData({ categoria: service.categoria, nome: service.nome, novaCategoria: '', nivel_tecnico: service.nivel_tecnico || 1 });
    setIsEditOpen(true);
  };

  const openDelete = (service) => {
    setSelectedService(service);
    setIsDeleteOpen(true);
  };

  if (loading) {
    return <div className="text-center py-8 text-zinc-400">Carregando...</div>;
  }

  // Group by category
  const grouped = {};
  const filtered = filterCategory === '_all' ? services : services.filter(s => s.categoria === filterCategory);
  filtered.forEach(s => {
    if (!grouped[s.categoria]) grouped[s.categoria] = [];
    grouped[s.categoria].push(s);
  });

  const totalAtivos = services.filter(s => s.ativo).length;
  const totalInativos = services.filter(s => !s.ativo).length;

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
          Gerenciar Servicos
        </h2>
        <Button
          onClick={() => {
            setFormData({ categoria: '', nome: '', novaCategoria: '' });
            setIsCreateOpen(true);
          }}
          className="bg-[#FFB800] text-black hover:bg-[#E6A600]"
          data-testid="add-service-button"
        >
          <Plus size={20} className="mr-2" />
          Novo Servico
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-4">
          <div className="text-zinc-400 text-sm">Total de Servicos</div>
          <div className="text-3xl font-bold text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }} data-testid="stat-total-services">
            {services.length}
          </div>
        </div>
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-4">
          <div className="text-zinc-400 text-sm">Ativos</div>
          <div className="text-3xl font-bold text-emerald-500" style={{ fontFamily: 'Barlow Condensed' }}>
            {totalAtivos}
          </div>
        </div>
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-4">
          <div className="text-zinc-400 text-sm">Categorias</div>
          <div className="text-3xl font-bold text-blue-500" style={{ fontFamily: 'Barlow Condensed' }}>
            {categories.length}
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-6">
        <FunnelSimple size={20} className="text-[#FFB800]" />
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-64 bg-[#121212] border-[#27272A] text-white" data-testid="filter-category-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-[#27272A] text-white">
            <SelectItem value="_all">Todas as categorias</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Services by Category */}
      <div className="space-y-6">
        {Object.keys(grouped).length === 0 ? (
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-8 text-center text-zinc-400">
            Nenhum servico encontrado
          </div>
        ) : (
          Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([categoria, items]) => (
            <div key={categoria} className="bg-[#121212] border border-[#27272A] rounded-sm">
              <div className="px-6 py-3 bg-[#050505] border-b border-[#27272A] flex justify-between items-center">
                <h3 className="text-lg font-semibold text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }}>
                  {categoria}
                </h3>
                <span className="text-zinc-400 text-sm">{items.length} servico(s)</span>
              </div>
              <div className="divide-y divide-[#27272A]">
                {items.map(service => (
                  <div
                    key={service.id}
                    className="px-6 py-3 flex items-center justify-between hover:bg-[#1E1E1E] transition-colors"
                    data-testid={`service-row-${service.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-sm ${service.ativo ? 'text-white' : 'text-zinc-600 line-through'}`}>
                        {service.nome}
                      </span>
                      <StarRating value={service.nivel_tecnico || 1} readOnly size={12} />
                      {!service.ativo && (
                        <Badge className="bg-zinc-700 text-zinc-400 text-xs">Inativo</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(service)}
                        className={`px-3 py-1 text-xs rounded-sm border transition-colors ${
                          service.ativo
                            ? 'border-emerald-600 text-emerald-500 hover:bg-emerald-900/20'
                            : 'border-zinc-600 text-zinc-400 hover:bg-zinc-800'
                        }`}
                        data-testid={`toggle-service-${service.id}`}
                      >
                        {service.ativo ? 'Ativo' : 'Inativo'}
                      </button>
                      <button
                        onClick={() => openEdit(service)}
                        className="p-2 text-zinc-400 hover:text-[#FFB800] transition-colors"
                        data-testid={`edit-service-${service.id}`}
                      >
                        <PencilSimple size={16} />
                      </button>
                      <button
                        onClick={() => openDelete(service)}
                        className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
                        data-testid={`delete-service-${service.id}`}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Novo Servico</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Categoria *</Label>
              <Select value={formData.categoria} onValueChange={(v) => setFormData({...formData, categoria: v, novaCategoria: ''})}>
                <SelectTrigger className="bg-[#050505] border-[#27272A] text-white" data-testid="service-category-select">
                  <SelectValue placeholder="Selecione uma categoria..." />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                  <SelectItem value="_new">+ Nova categoria</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.categoria === '_new' && (
              <div>
                <Label>Nome da Nova Categoria *</Label>
                <Input
                  value={formData.novaCategoria}
                  onChange={(e) => setFormData({...formData, novaCategoria: e.target.value})}
                  className="bg-[#050505] border-[#27272A] text-white"
                  placeholder="Ex: Automacao, Energia Solar..."
                  required
                  data-testid="new-category-input"
                />
              </div>
            )}

            <div>
              <Label>Nome do Servico *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                className="bg-[#050505] border-[#27272A] text-white"
                placeholder="Ex: Instalacao de painel solar"
                required
                data-testid="service-name-input"
              />
            </div>

            <div>
              <Label>Nivel Tecnico Necessario *</Label>
              <div className="flex items-center gap-3 mt-1">
                <StarRating value={formData.nivel_tecnico} onChange={(v) => setFormData({...formData, nivel_tecnico: v})} size={24} />
                <span className="text-zinc-400 text-sm">{formData.nivel_tecnico} estrela(s)</span>
              </div>
              <p className="text-zinc-500 text-xs mt-1">Apenas prestadores com nivel igual ou superior poderao ser atribuidos.</p>
            </div>

            <Button type="submit" className="w-full bg-[#FFB800] text-black hover:bg-[#E6A600]" data-testid="submit-service-button">
              Criar Servico
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Servico</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <Label>Categoria</Label>
              <Select value={formData.categoria} onValueChange={(v) => setFormData({...formData, categoria: v, novaCategoria: ''})}>
                <SelectTrigger className="bg-[#050505] border-[#27272A] text-white" data-testid="edit-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                  <SelectItem value="_new">+ Nova categoria</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.categoria === '_new' && (
              <div>
                <Label>Nome da Nova Categoria</Label>
                <Input
                  value={formData.novaCategoria}
                  onChange={(e) => setFormData({...formData, novaCategoria: e.target.value})}
                  className="bg-[#050505] border-[#27272A] text-white"
                  placeholder="Nova categoria..."
                  data-testid="edit-new-category-input"
                />
              </div>
            )}

            <div>
              <Label>Nome do Servico</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                className="bg-[#050505] border-[#27272A] text-white"
                data-testid="edit-service-name-input"
              />
            </div>

            <div>
              <Label>Nivel Tecnico Necessario</Label>
              <div className="flex items-center gap-3 mt-1">
                <StarRating value={formData.nivel_tecnico} onChange={(v) => setFormData({...formData, nivel_tecnico: v})} size={24} />
                <span className="text-zinc-400 text-sm">{formData.nivel_tecnico} estrela(s)</span>
              </div>
            </div>

            <Button type="submit" className="w-full bg-[#FFB800] text-black hover:bg-[#E6A600]" data-testid="submit-edit-service-button">
              Salvar Alteracoes
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Excluir Servico</DialogTitle>
          </DialogHeader>
          {selectedService && (
            <div className="space-y-4">
              <p className="text-zinc-400">
                Tem certeza que deseja excluir o servico abaixo?
              </p>
              <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm">
                <div className="text-zinc-500 text-xs">Categoria</div>
                <div className="text-white">{selectedService.categoria}</div>
                <div className="text-zinc-500 text-xs mt-2">Servico</div>
                <div className="text-[#FFB800] font-medium">{selectedService.nome}</div>
              </div>
              <p className="text-red-400 text-xs">
                Esta acao nao pode ser desfeita. Solicitacoes existentes nao serao afetadas.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => setIsDeleteOpen(false)}
                  className="flex-1 bg-[#1E1E1E] hover:bg-[#27272A]"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-service-button"
                >
                  Excluir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceManagement;
